import React, { useCallback } from 'react';

import AutoTagsField from 'components/AutoTagsField';
import ExpandableCard from 'components/ExpandableCard';

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloseIcon from '@mui/icons-material/Close';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { useField, useManager, useMultiFields } from 'app-components/flux/transaction-rules/transactionRulesFormHooks';
import { useStateContext } from 'app-components/flux/transaction-rules/transactionRulesFormState';
import type {
  PayeeMatchingOperatorType,
  PayeeNameType,
} from 'app-components/flux/transaction-rules/transactionRulesTypes';
import { MAX_OR_CONDITIONS } from 'app-components/flux/transaction-rules/transactionRulesTypes';
import InfoTooltipIcon from 'components/InfoTooltipIcon';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => ({
  row: {
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  column: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  columnSpacing: {
    gap: theme.spacing(1),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(1),
  },
  card: {
    border: `1px ${theme.palette.border.light} solid`,
    background: theme.palette.background.default,
    '& .MuiCardHeader-root': {
      height: 52,
      borderBottom: `1px solid ${theme.palette.border.light}`,
      paddingTop: theme.spacing(1.5),
      paddingBottom: theme.spacing(1.5),
    },
    marginTop: 0,
  },
  tags: {
    margin: 0,
    '.MuiFilledInput-root': {
      minHeight: '52px',
      boxSizing: 'border-box',
      background: theme.palette.background.paper,
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
    },
  },
  left: {
    maxWidth: 150,
  },
  selectComponent: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '52px',
    boxSizing: 'border-box',
    '& .MuiOutlinedInput-notchedOutline': {
      border: `1px solid ${theme.palette.border.light}`,
    },
  },
  highlighted: {
    color: theme.palette.primary.main,
  },
  editableField: {
    '#payee-match-text': {
      background: theme.palette.background.paper,
      padding: theme.spacing(2),
      borderRadius: 12,
      height: 20,
    },
  },
  addBtnRow: {
    display: 'flex',
    flex: 1,
    gap: 16,
    '> hr': {
      flex: 1,
      alignSelf: 'center',
    },
  },
  resetBtn: {
    color: theme.palette.primary.main,
    marginLeft: theme.spacing(1),
    cursor: 'pointer',
  },
  alignItemsStart: {
    alignItems: 'flex-start',
  },
}));

type HighlightableTypographyProps = {
  value: string;
  match: string;
  children: React.ReactNode;
};

const HighlightableTypography = ({ value, match, children }: HighlightableTypographyProps) => {
  const { classes } = useStyles();
  // Highlight the text if it matches the value
  return (
    <Typography className={value === match ? classes.highlighted : undefined} variant='paragraphRegular'>
      {children}
    </Typography>
  );
};

const PayeeSourceNameType: React.FC = () => {
  const { classes } = useStyles();
  const { value, onValueChange } = useField('payeeSourceType');
  const { onValueChange: onPayeeSourceNameChange } = useMultiFields('payeeSourceName', 0);
  const { state } = useStateContext();
  const { fromTxn, statementPayeeName, inferredPayeeName } = state;

  return (
    <Box className={classes.row} sx={{ mb: 2 }}>
      <Box key={value} className={classes.column} sx={{ flex: 0 }}>
        <FormControl component='fieldset'>
          <RadioGroup
            aria-label='options'
            name='options'
            value={value}
            onChange={(_event, value) => {
              onValueChange(value as PayeeNameType);
              // if the user changes the payeeSourceType and we are createing a rule form a transaction, then we need to modify the payeeSourceName
              // from the txn whenever the user toggles the payeeSourceType
              if (fromTxn && statementPayeeName && inferredPayeeName) {
                onPayeeSourceNameChange(value === 'STATEMENT' ? statementPayeeName : inferredPayeeName);
              }
            }}
            sx={{ gap: 0.5 }}
          >
            <FormControlLabel
              id='payee-source-statement-name'
              value='STATEMENT'
              control={<Radio sx={{ py: 0 }} />}
              label={
                <HighlightableTypography value={value} match='STATEMENT'>
                  Original statement name
                </HighlightableTypography>
              }
            />
            <FormControlLabel
              id='payee-source-inferred-name'
              value='INFERRED'
              control={<Radio sx={{ py: 0 }} />}
              label={
                <HighlightableTypography value={value} match='INFERRED'>
                  Quicken name
                </HighlightableTypography>
              }
            />
          </RadioGroup>
        </FormControl>
      </Box>
      <Divider orientation='vertical' variant='middle' flexItem></Divider>
      <Box className={classes.column} sx={{ pl: 1.5 }}>
        {value === 'STATEMENT' && !fromTxn && (
          <Typography variant='captionRegular'>
            Recommended: this is the payee name that shows up on your bank statement.
          </Typography>
        )}
        {value === 'STATEMENT' && fromTxn && (
          <Stack>
            <Stack direction='row' alignItems='center' gap={1}>
              <Typography variant='captionRegular'>Original statement name</Typography>
              <InfoTooltipIcon
                size='small'
                // className={classes.infoIcon}
                tooltipPlacement='right'
                tooltipText='Recommended: this is the payee name that shows up on your bank statement.'
              />
            </Stack>
            <Typography variant='captionRegular'>{statementPayeeName}</Typography>
          </Stack>
        )}
        {value === 'INFERRED' && !fromTxn && (
          <Typography variant='captionRegular'>This is the payee name that shows up in Quicken.</Typography>
        )}
        {value === 'INFERRED' && fromTxn && (
          <Stack>
            <Stack direction='row' alignItems='center' gap={1}>
              <Typography variant='captionRegular'>Quicken name</Typography>
              <InfoTooltipIcon
                size='small'
                // className={classes.infoIcon}
                tooltipPlacement='right'
                tooltipText='This is the payee name that shows up in Quicken.'
              />
            </Stack>
            <Typography variant='captionRegular'>{inferredPayeeName}</Typography>
          </Stack>
        )}
      </Box>
    </Box>
  );
};

interface PayeeMatchCriteriaListItemTypeProps {
  index: number;
}

type MatchOperatorEvent = { target: { value: PayeeMatchingOperatorType } };

const PayeeMatchCriteriaListItemType: React.FC<PayeeMatchCriteriaListItemTypeProps> = ({ index }) => {
  const { classes } = useStyles();
  const { value, onChange } = useMultiFields('payeeSourceMatchingOperator', index);

  return (
    <FormControl>
      <Select
        id='payee-match-select'
        value={value || 'CONTAINS'}
        className={classes.selectComponent}
        onChange={(e) => onChange(e as MatchOperatorEvent)}
      >
        <MenuItem value='CONTAINS'>Contains</MenuItem>
        <MenuItem value='EQUALS'>Is exactly</MenuItem>
      </Select>
    </FormControl>
  );
};

interface PayeeMatchCriteriaListItemNameProps {
  index: number;
  matchOperator: PayeeMatchingOperatorType;
}

const PayeeMatchCriteriaListItemName: React.FC<PayeeMatchCriteriaListItemNameProps> = ({ index, matchOperator }) => {
  const { classes } = useStyles();
  const { value: payeeName, onValueChange, error, helperText } = useMultiFields('payeeSourceName', index);
  const { state } = useStateContext();
  const { fromTxn } = state;

  const getKeywords = (value: string) =>
    value && value.trim().length > 0
      ? value
          ?.toLowerCase()
          .split(' ')
          .filter((token: string) => token !== '')
      : [];

  const value = getKeywords(payeeName);

  // only show placeholder if there is no value
  const keywordPlaceHolder = value.length ? undefined : 'Keywords';
  const exactMatchPlaceHolder = value.length ? undefined : 'Exact payee';

  return (
    <>
      {matchOperator === 'CONTAINS' ? (
        <AutoTagsField
          id='payee-match-keywords'
          value={value}
          onChange={(_e: any, value: string[]) => onValueChange(value.join(' '))}
          separator=' '
          autoFocus
          label={undefined}
          placeholder={keywordPlaceHolder}
          margin='normal'
          variant='filled'
          className={classes.tags}
          error={error}
          helperText={helperText}
        />
      ) : (
        <TextField
          id='payee-match-text'
          variant='filled'
          defaultValue={payeeName}
          label={undefined}
          placeholder={exactMatchPlaceHolder}
          InputProps={{
            disableUnderline: true,
          }}
          className={classes.editableField}
          onBlur={(event) => onValueChange(event.target.value)}
          error={error}
          helperText={helperText}
          disabled={fromTxn}
        />
      )}
    </>
  );
};

const PayeeMatchCriteriaList: React.FC = () => {
  const { classes, cx } = useStyles();
  const manager = useManager();
  const { fireAction } = manager;
  const { value: payeeSourceCondition, onValueChange } = useField('payeeSource');
  const { state } = useStateContext();
  const { fromTxn, statementPayeeName, inferredPayeeName, rule } = state;

  const onResetHandler = useCallback(() => {
    // revert to original values, but keep the current the payeeType
    const resetObj: { payeeMatchingOperator: PayeeMatchingOperatorType; payeeName: string } = {
      payeeMatchingOperator: 'CONTAINS',
      payeeName: (payeeSourceCondition.payeeType === 'STATEMENT' ? statementPayeeName : inferredPayeeName) || '',
    };
    const payeeMatchingCriteria = fromTxn && [resetObj];

    const reset = rule &&
      rule.payeeSourceCondition?.payeeMatchingCriteria && {
        payeeMatchingCriteria: payeeMatchingCriteria || rule.payeeSourceCondition?.payeeMatchingCriteria,
        payeeType: payeeSourceCondition.payeeType,
      };
    reset && onValueChange(reset);
  }, [fromTxn, inferredPayeeName, onValueChange, payeeSourceCondition.payeeType, rule, statementPayeeName]);

  return (
    <Box className={cx(classes.column, classes.columnSpacing)}>
      {payeeSourceCondition.payeeMatchingCriteria?.map((criterion, index, array) => {
        const instructionTxt =
          criterion.payeeMatchingOperator === 'CONTAINS'
            ? 'Commas or spaces enter a new keyword. Order them how you expect them to appear.'
            : fromTxn
              ? '“Exactly” conditions based off a transaction are not editable.'
              : 'Enter the exact payee that transactions must have for the rule to take effect.';

        return (
          <Box key={`${criterion.payeeName}_${index}`}>
            <Box className={classes.row} sx={{ mb: 1 }}>
              <Typography variant='captionRegular'>{instructionTxt}</Typography>
              {fromTxn && criterion.payeeMatchingOperator === 'CONTAINS' && (
                <Typography variant='button' className={classes.resetBtn} onClick={onResetHandler}>
                  Reset
                </Typography>
              )}
            </Box>
            <Box key={criterion.payeeName} className={classes.column}>
              <Box className={cx(classes.row, classes.alignItemsStart)}>
                <Box className={cx(classes.column, classes.left)}>
                  <PayeeMatchCriteriaListItemType index={index as number} />
                </Box>
                <Box className={classes.column}>
                  <PayeeMatchCriteriaListItemName
                    index={index as number}
                    matchOperator={criterion.payeeMatchingOperator as PayeeMatchingOperatorType}
                  />
                </Box>
                <Box sx={{ alignContent: 'flex-start' }}>
                  {index === 0 && array.length === 1 ? (
                    <AddCircleOutlineIcon
                      id={`add-payee-source`}
                      sx={{ minHeight: 56 }}
                      onClick={() =>
                        fireAction('addPayeeSourceMatchingCriteria', {
                          payeeMatchingOperator: 'CONTAINS',
                          payeeName: undefined,
                        })
                      }
                    />
                  ) : (
                    <CloseIcon
                      id={`remove-payee-soure-${index}`}
                      onClick={(_event) => fireAction('removePayeeSourceMatchingCriteria', index)}
                      sx={{ minHeight: 56 }}
                    />
                  )}
                </Box>
              </Box>
              {index + 1 < array.length && (
                <Box className={classes.row}>
                  <Typography variant='overlineRegular'>OR</Typography>
                  <Divider sx={{ flex: 1, margin: 'auto !important' }} />
                </Box>
              )}
              {array.length < MAX_OR_CONDITIONS && index + 1 === array.length && array.length != 1 && (
                <Box className={classes.addBtnRow}>
                  <Divider />
                  <AddCircleOutlineIcon
                    id={`add-payee-source`}
                    onClick={() =>
                      fireAction('addPayeeSourceMatchingCriteria', {
                        payeeMatchingOperator: 'CONTAINS',
                        payeeName: undefined,
                      })
                    }
                  />
                </Box>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export const PayeeSourceCardContent: React.FC = () => {
  return (
    <Box id='payee-source-condition'>
      <PayeeSourceNameType />
      <Divider orientation='horizontal'></Divider>
      <PayeeMatchCriteriaList />
    </Box>
  );
};

export function PayeeSourceCard(props: any) {
  const { classes, theme } = useStyles();

  const { value, onValueChange } = useField('isEnabledPayeeSource');

  const toggleEnabled = () => {
    onValueChange(!value);
  };

  return (
    <ExpandableCard
      id='rule-payee-source'
      className={classes.card}
      title='Payee'
      cardHeaderProps={{ padding: `${theme.spacing(1.5)} ${theme.spacing(2)}` }}
      titleProps={{ variant: 'subtitle2' }}
      expandIcon={<Checkbox id='rule-payee-source-enabled' checked={value} onClick={toggleEnabled}></Checkbox>}
      isExpanded={value || !!props.hideToggle}
      hideToggle={!!props.hideToggle}
    >
      <PayeeSourceCardContent />
    </ExpandableCard>
  );
}
